import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import InstallAppButton from '../components/shared/InstallAppButton';
import { 
  FaChartLine, FaShieldAlt, FaBullseye, FaRocket, FaUsers, FaBrain,
  FaGraduationCap, FaBook, FaVideo, FaBookOpen, FaLock, FaGift,
  FaClipboardCheck, FaCheck, FaWhatsapp, FaEnvelope, FaPhone,
  FaSpinner, FaEye, FaEyeSlash, FaStar, FaArrowRight, FaPlay,
  FaChartPie, FaCalculator, FaHandshake, FaHeart, FaHome, FaChild,
  FaExclamationTriangle, FaLightbulb, FaWallet, FaUserShield, FaBalanceScale,
  FaRegLightbulb, FaCheckCircle, FaQuestionCircle, FaCreditCard, FaTimes,
  FaChevronDown
} from 'react-icons/fa';

// "Talk to your consultant" CTA — replaces the previous Razorpay link everywhere
// on the landing page. We no longer expose direct payment from marketing pages.
// The phrasing below is intentionally non-pushy so it works in CTA, pricing card
// and sticky-footer contexts.
const TalkToConsultantButton = ({ size = 'large', className = '' }) => {
  const sizing = size === 'large'
    ? 'px-6 py-3 text-base'
    : 'px-4 py-2 text-sm';

  return (
    <a
      href="#contact"
      data-testid="talk-to-consultant-btn"
      className={`inline-flex items-center justify-center gap-2 rounded-lg font-semibold text-[#263238] bg-gradient-to-r from-[#D4A84F] to-[#B18A3A] hover:from-[#DBB055] hover:to-[#D4A84F] shadow-md shadow-[#D4A84F]/30 transition-all transform hover:scale-[1.02] ${sizing} ${className}`}
    >
      <FaHandshake />
      <span>Talk to your consultant</span>
    </a>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showDataSecurityModal, setShowDataSecurityModal] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error || 'Login failed');
    }
    
    setLoading(false);
  };

  const features = [
    {
      icon: FaChartPie,
      title: "Goal-Based Financial Dashboard",
      description: "A complete 360° view mapping your Income, Expenses, Assets, Liabilities, Insurance & Investments—all aligned to your life goals.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: FaBullseye,
      title: "Scientific Gap Identification",
      description: "No guesswork. Identify Life Insurance, Health Insurance, Retirement, Child Education & Wealth Creation gaps instantly.",
      color: "from-red-500 to-orange-500"
    },
    {
      icon: FaCalculator,
      title: "Risk Profiler",
      description: "Understand your real risk-taking ability. Get the right asset allocation mix based on your financial personality.",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: FaRocket,
      title: "LIVE Goal Progress Tracking",
      description: "Track your goals in real-time with visual charts. Know if you're on track, behind schedule, or overfunded.",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: FaHandshake,
      title: "Personalised Consultation",
      description: "Access certified financial consultants for regular reviews, progress checks, strategy corrections & expert guidance.",
      color: "from-amber-500 to-yellow-500"
    },
    {
      icon: FaBrain,
      title: "AI-Powered Dashboard",
      description: "Comprehensive financial reports powered by intelligent analytics with visual charts & structured recommendations.",
      color: "from-indigo-500 to-violet-500"
    },
    {
      icon: FaGraduationCap,
      title: "Money School",
      description: "Personal finance education in regional languages. Kid Zone to teach money through games & stories for the whole family.",
      color: "from-teal-500 to-cyan-500"
    },
    {
      icon: FaBook,
      title: "Paiso Ki Pathshala",
      description: "Presentation-based learning modules on Insurance, Investments, Wealth Creation & Risk Management.",
      color: "from-rose-500 to-pink-500"
    },
    {
      icon: FaVideo,
      title: "Training Videos",
      description: "Short, structured videos on Budgeting, Insurance, SIPs, Retirement planning & Goal-based planning.",
      color: "from-sky-500 to-blue-500"
    },
    {
      icon: FaBookOpen,
      title: "eBooks Library",
      description: "Practical eBooks on Insurance planning, Retirement, Child education, Wealth creation & Protection strategies.",
      color: "from-lime-500 to-green-500"
    },
    {
      icon: FaLock,
      title: "My Family Should Know",
      description: "Secure vault to record Investments, Insurance, Loans, Real estate, Gold & important documents for emergencies.",
      color: "from-slate-500 to-gray-600"
    },
    {
      icon: FaGift,
      title: "Free Value-Added Services",
      description: "Access Shiksha Sankalp, Goal Manthan, My Money Personality Test & more special initiatives.",
      color: "from-fuchsia-500 to-purple-500"
    }
  ];

  const benefits = [
    { icon: FaCheck, text: "Clarity" },
    { icon: FaCheck, text: "Structure" },
    { icon: FaCheck, text: "Scientific Planning" },
    { icon: FaCheck, text: "Expert Guidance" },
    { icon: FaCheck, text: "Financial Education" },
    { icon: FaCheck, text: "Family Security" },
    { icon: FaCheck, text: "Peace of Mind" }
  ];

  const financialChallenges = [
    {
      id: 1,
      problem: "The \"GOK Account\" Problem",
      subtitle: "(God Only Knows Where the Money Goes)",
      description: "Many families experience a mismatch between monthly income and expenses. There is no structured tracking, no visibility, and no financial direction.",
      painPoints: [
        "Savings become inconsistent",
        "Goals get delayed",
        "Stress increases despite steady income"
      ],
      solution: "Money Meter",
      solutionDesc: "Money Meter provides a structured budgeting and financial health dashboard. It tracks income, classifies expenses, measures savings efficiency, and highlights financial stress indicators. Instead of guessing, you operate with clarity and control.",
      icon: FaWallet,
      color: "from-red-500 to-orange-500"
    },
    {
      id: 2,
      problem: "Buying Random Products Instead of Achieving Goals",
      subtitle: "",
      description: "Insurance policies, mutual funds, ULIPs, FDs — products are purchased without linking them to specific life goals.",
      painPoints: [
        "Scattered investments",
        "Overlapping products",
        "Underfunded priorities like retirement or child education"
      ],
      solution: "Goal-Based Planning",
      solutionDesc: "Arth Gurukul maps every rupee to a defined purpose — retirement, education, marriage, wealth creation. It identifies funding gaps and calculates required monthly investments, ensuring every decision moves you closer to a life goal.",
      icon: FaBullseye,
      color: "from-purple-500 to-pink-500"
    },
    {
      id: 3,
      problem: "Everyone Is Selling — But Who Is Solving?",
      subtitle: "",
      description: "Most people buy products, not solutions. Sales conversations often focus on features, not financial needs.",
      painPoints: [
        "Inadequate insurance coverage",
        "Incorrect asset allocation",
        "Major goal shortfalls"
      ],
      solution: "Need-Based Consultation",
      solutionDesc: "Arth Gurukul connects you with qualified financial consultants and planners who focus on need-based analysis. The approach is diagnostic first, recommendation second — ensuring solutions align with your real financial priorities.",
      icon: FaUserShield,
      color: "from-blue-500 to-cyan-500"
    },
    {
      id: 4,
      problem: "Rising Unclaimed Money in Financial Institutions",
      subtitle: "",
      description: "Across India, unclaimed funds in banks, insurance companies, and financial institutions continue to rise.",
      painPoints: [
        "No consolidated documentation",
        "Missing nomination details",
        "Family unaware of investments"
      ],
      solution: "\"My Family Should Know\" Data Organiser",
      solutionDesc: "This structured digital organiser records financial accounts, policies, nominations, liabilities, and key documents in one place. It ensures your family knows what exists, where it exists, and how to access it — when it matters most.",
      icon: FaLock,
      color: "from-slate-500 to-gray-600"
    },
    {
      id: 5,
      problem: "Low Financial Literacy",
      subtitle: "",
      description: "Limited financial awareness makes individuals vulnerable to mis-selling, unrealistic return promises, and financial frauds.",
      painPoints: [
        "Risk is misunderstood",
        "Returns are overestimated",
        "Protection is ignored"
      ],
      solution: "Money School",
      solutionDesc: "Learn the fundamentals of personal finance and money management in your own language through structured eBooks, presentations, and interactive learning modules. Financial literacy becomes practical, simple, and actionable.",
      icon: FaGraduationCap,
      color: "from-green-500 to-emerald-500"
    },
    {
      id: 6,
      problem: "Misinformation & Social Media Noise",
      subtitle: "",
      description: "The internet is full of half-truths, viral advice, and unverified financial opinions.",
      painPoints: [
        "Poor investment decisions",
        "Excessive risk-taking",
        "Missed long-term opportunities"
      ],
      solution: "Paiso Ki Pathshala - Guided Self-Learning with Expert Support",
      solutionDesc: "Arth Gurukul combines self-learning tools with guided support from qualified consultants, coaches, and financial experts. Decisions are based on analysis — not trends or speculation.",
      icon: FaBalanceScale,
      color: "from-amber-500 to-yellow-500"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8F6F0] via-[#FDFCF9] to-[#F8F6F0] font-inter text-[#263238]">
      {/* Header with Logo */}
      <header data-testid="landing-header" className="sticky top-0 z-50 bg-[#0F4C5C]/95 backdrop-blur-md border-b border-[#D4A84F]/30 shadow-lg shadow-[#0F4C5C]/25">
        <div className="container mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="header-logo">
            <img
              src="/arth-gurukul-logo.png"
              alt="Arth Gurukul Logo"
              className="h-12 w-12 rounded-full object-contain bg-white ring-2 ring-[#D4A84F]/40 p-0.5"
            />
            <div className="hidden sm:block leading-tight">
              <span className="font-poppins text-lg font-semibold text-[#D4A84F] tracking-wide">Arth Gurukul</span>
              <p className="text-[10px] text-white/70 tracking-widest uppercase">Your School for Lifelong Financial Wellness</p>
            </div>
          </div>
          <nav className="flex items-center gap-4 sm:gap-6">
            <a href="#features" className="text-sm text-white/85 hover:text-[#D4A84F] transition-colors hidden md:inline">Features</a>
            <a href="#why-arth-kundli" className="text-sm text-white/85 hover:text-[#D4A84F] transition-colors hidden md:inline">Why Us</a>
            <a href="#pricing" className="text-sm text-white/85 hover:text-[#D4A84F] transition-colors hidden md:inline">Pricing</a>
            <button
              onClick={() => document.getElementById('login-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-5 py-2 bg-[#D4A84F] text-[#263238] text-sm font-semibold rounded-full hover:bg-[#B18A3A] hover:text-white transition-all shadow-md shadow-[#D4A84F]/30"
              data-testid="header-login-btn"
            >
              Login
            </button>
          </nav>
        </div>
      </header>

      {/* Attention-Grabbing Top Banner */}
      <div className="relative overflow-hidden bg-gradient-to-r from-[#0F4C5C] via-[#093039] to-[#0F4C5C] border-b border-[#D4A84F]/30">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full">
            <div className="absolute top-1/2 left-1/4 w-2 h-2 bg-[#D4A84F] rounded-full animate-ping opacity-60"></div>
            <div className="absolute top-1/3 left-1/2 w-2 h-2 bg-[#D4A84F] rounded-full animate-ping opacity-40 animation-delay-300"></div>
            <div className="absolute top-2/3 left-3/4 w-2 h-2 bg-[#D4A84F] rounded-full animate-ping opacity-50 animation-delay-700"></div>
            <div className="absolute top-1/2 right-1/4 w-1.5 h-1.5 bg-white rounded-full animate-pulse opacity-30"></div>
            <div className="absolute top-1/4 right-1/3 w-1.5 h-1.5 bg-white rounded-full animate-pulse opacity-20 animation-delay-500"></div>
          </div>
          {/* Subtle moving gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#D4A84F]/5 to-transparent animate-shimmer"></div>
        </div>
        
        <div className="relative z-10 py-4 md:py-5">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-center">
              {/* Heart Icons with pulse */}
              <div className="hidden sm:flex items-center space-x-1 mr-4">
                <FaHeart className="text-red-500 text-sm animate-pulse" />
                <FaHeart className="text-red-400 text-xs animate-pulse animation-delay-200" />
                <FaHeart className="text-red-500 text-sm animate-pulse animation-delay-400" />
              </div>
              
              {/* Main Text with animation */}
              <div className="text-center">
                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold tracking-wide">
                  <span className="bg-gradient-to-r from-[#D4A84F] via-white to-[#D4A84F] bg-clip-text text-transparent animate-gradient-x">
                    "Because My Family Deserves Certainty, Not Assumptions."
                  </span>
                </h2>
              </div>
              
              {/* Heart Icons with pulse */}
              <div className="hidden sm:flex items-center space-x-1 ml-4">
                <FaHeart className="text-red-500 text-sm animate-pulse animation-delay-400" />
                <FaHeart className="text-red-400 text-xs animate-pulse animation-delay-200" />
                <FaHeart className="text-red-500 text-sm animate-pulse" />
              </div>
            </div>
            
            {/* Decorative line */}
            <div className="flex justify-center mt-3">
              <div className="flex items-center space-x-2">
                <div className="w-8 md:w-12 h-0.5 bg-gradient-to-r from-transparent to-[#D4A84F] rounded-full"></div>
                <FaStar className="text-[#D4A84F] text-xs animate-spin-slow" />
                <div className="w-8 md:w-12 h-0.5 bg-gradient-to-l from-transparent to-[#D4A84F] rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom glow effect */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4A84F]/50 to-transparent"></div>
      </div>

      {/* Hero Section with Login */}
      <section id="login-section" className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#D4A84F]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-[#D4A84F]/5 to-blue-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 py-12 lg:py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left - Content */}
            <div className="text-center lg:text-left order-2 lg:order-1">
              <div className="inline-flex items-center px-4 py-2 bg-[#0F4C5C] rounded-full mb-6 shadow-md shadow-[#0F4C5C]/25">
                <FaStar className="text-[#D4A84F] mr-2" />
                <span className="text-white text-sm font-semibold tracking-wide">Your Complete Financial Command Centre</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
                <span className="text-[#D4A84F]">Arth Gurukul</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-[#4E5B65] mb-6">
                Your School for Lifelong Financial Wellness
              </p>
              
              <p className="text-lg text-[#78838C] mb-8 max-w-xl mx-auto lg:mx-0">
                Choose how you learn. Start <span className="text-[#0F4C5C] font-semibold">Free</span>, go <span className="text-[#16A085] font-semibold">DIY</span> at <span className="text-[#B18A3A] font-semibold">₹999 + GST/year</span>, or get hand-held with our <span className="text-[#B18A3A] font-semibold">Guided</span> plan at <span className="text-[#B18A3A] font-semibold">₹2,999 + GST/year</span> (regular ₹4,999). Education, money management tools and expert advisors — all in one scientific financial wellness ecosystem.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                <TalkToConsultantButton size="large" className="inline-flex items-center justify-center" />
                <a 
                  href="#features"
                  className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-[#D4A84F] text-[#D4A84F] font-bold rounded-xl hover:bg-[#D4A84F]/10 transition-all"
                >
                  <FaPlay className="mr-2" />
                  Explore Features
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto lg:mx-0">
                <div className="text-center p-4 bg-white/5 rounded-xl backdrop-blur-sm">
                  <div className="text-2xl md:text-3xl font-bold text-[#D4A84F]">14+</div>
                  <div className="text-xs text-[#78838C]">Premium Features</div>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-xl backdrop-blur-sm">
                  <div className="text-2xl md:text-3xl font-bold text-[#D4A84F]">360°</div>
                  <div className="text-xs text-[#78838C]">Financial View</div>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-xl backdrop-blur-sm">
                  <div className="text-2xl md:text-3xl font-bold text-[#D4A84F]">24/7</div>
                  <div className="text-xs text-[#78838C]">Access</div>
                </div>
              </div>
            </div>

            {/* Right - Login Form */}
            <div className="order-1 lg:order-2">
              <div className="bg-white rounded-2xl p-8 border border-[#EDE9DE] shadow-xl shadow-[#0F4C5C]/10 max-w-md mx-auto">
                <div className="text-center mb-6">
                  <h2 className="font-poppins text-2xl font-semibold text-[#0F4C5C] mb-2">Sign In</h2>
                  <p className="text-[#4E5B65] text-sm">Access your financial dashboard</p>
                </div>

                {/* Info Banner */}
                <div className="flex items-center justify-center w-full py-3 mb-6 bg-gradient-to-r from-[#D4A84F] to-[#B18A3A] text-[#263238] font-semibold rounded-xl shadow-md">
                  Contact Your Financial Consultant
                </div>

                <div className="flex items-center mb-6">
                  <div className="flex-1 h-px bg-[#EDE9DE]"></div>
                  <span className="px-4 text-[#78838C] text-sm">or sign in</span>
                  <div className="flex-1 h-px bg-[#EDE9DE]"></div>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm text-center">
                    {error}
                  </div>
                )}

                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block text-[#263238] text-sm font-medium mb-2">Email Address</label>
                    <div className="relative">
                      <FaEnvelope className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#78838C]" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="advisor@example.com"
                        className="w-full pl-12 pr-4 py-3 bg-[#F8F6F0] border border-[#EDE9DE] rounded-xl text-[#263238] placeholder-[#A5ACB2] focus:border-[#0F4C5C] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/15 transition-all"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#263238] text-sm font-medium mb-2">Password</label>
                    <div className="relative">
                      <FaLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#78838C]" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="••••••••"
                        className="w-full pl-12 pr-12 py-3 bg-[#F8F6F0] border border-[#EDE9DE] rounded-xl text-[#263238] placeholder-[#A5ACB2] focus:border-[#0F4C5C] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/15 transition-all"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#78838C] hover:text-[#0F4C5C]"
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-[#0F4C5C] text-white font-semibold rounded-xl hover:bg-[#093039] transition-all flex items-center justify-center shadow-md shadow-[#0F4C5C]/25"
                  >
                    {loading ? (
                      <>
                        <FaSpinner className="animate-spin mr-2" />
                        Signing In...
                      </>
                    ) : (
                      <>
                        Sign In
                        <FaArrowRight className="ml-2" />
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-4 text-center">
                  <Link to="/forgot-password" className="text-[#D4A84F] text-sm hover:underline">
                    Forgot Password?
                  </Link>
                </div>

                <div className="mt-4 text-center text-[#78838C] text-sm">
                  Don't have an account?{' '}
                  <Link to="/register" className="text-[#D4A84F] hover:underline font-medium">
                    Register here
                  </Link>
                </div>

                {/* Contact Info */}
                <div className="mt-6 pt-6 border-t border-[#EDE9DE]">
                  <p className="text-[#78838C] text-sm text-center mb-3">Need Help? Contact Us</p>
                  <div className="flex justify-center gap-3 text-sm flex-wrap">
                    <span className="text-[#4E5B65]">Talk to Your Consultant</span>
                    <span className="text-[#78838C]">|</span>
                    <span className="text-[#4E5B65]">Planner or Manager</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-[#D4A84F]/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-[#D4A84F] rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Hero Banner Image */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#F8F6F0] to-white" data-testid="hero-banner-section">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#D4A84F]/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 py-10 md:py-14 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden border border-[#D4A84F]/20 shadow-2xl shadow-[#D4A84F]/10 group">
              <div className="absolute inset-0 bg-gradient-to-t from-[#F8F6F0]/60 via-transparent to-transparent z-10 pointer-events-none"></div>
              <img
                src="https://i.ibb.co/XrLfZHBq/Arth-Gurukul-YT.png"
                alt="Arth Gurukul — AI-powered financial education and planning tools"
                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                data-testid="hero-banner-image"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* DIY vs Guided Section */}
      <section className="py-20 relative overflow-hidden" data-testid="diy-guided-section">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-10 w-72 h-72 bg-[#D4A84F]/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-10 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-2 bg-[#0F4C5C] text-white rounded-full text-sm font-semibold mb-4 shadow-md shadow-[#0F4C5C]/25 tracking-wide">
              Choose Your Path
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#263238] mb-4">
              How Do You Want to <span className="text-[#D4A84F]">Plan Your Finances</span>?
            </h2>
            <p className="text-[#78838C] text-base max-w-2xl mx-auto">
              Whether you prefer full control or expert hand-holding, Arth Gurukul adapts to your style.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {/* DIY Card */}
            <div className="relative group" data-testid="diy-card">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-[#16A085] to-[#0D6A57] rounded-2xl opacity-15 group-hover:opacity-30 blur transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-2xl p-8 border border-[#16A085]/20 hover:border-[#16A085]/45 transition-all h-full flex flex-col shadow-lg shadow-[#0F4C5C]/5">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#16A085] to-[#0D6A57] flex items-center justify-center flex-shrink-0 shadow-md shadow-[#16A085]/30">
                    <FaRocket className="text-white text-xl" />
                  </div>
                  <div>
                    <h3 className="font-poppins text-2xl font-semibold text-[#0F4C5C]">DIY</h3>
                    <p className="text-[#16A085] text-sm font-medium">Do It Yourself</p>
                  </div>
                </div>

                <p className="text-[#4E5B65] text-sm mb-6 leading-relaxed">
                  Take full control of your financial journey. Access powerful tools, data-driven insights, and self-paced learning modules to plan, track, and grow your wealth independently.
                </p>

                <div className="space-y-3 mb-8 flex-grow">
                  {[
                    "360° Financial Dashboard & Goal Tracker",
                    "Scientific Gap Analysis & Net Worth Snapshot",
                    "AI-Powered Smart Insights & Suggestions",
                    "Money Meter — Income, Expenses & Cash Leak Detection",
                    "Financial Education Modules (Self-Paced)",
                    "Secure Financial Vault for Documents",
                    "Child Future Ready Assessment Tool",
                    "All Platform Updates & New Features"
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <FaCheck className="text-[#16A085] mt-1 flex-shrink-0 text-xs" />
                      <span className="text-[#263238] text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-auto pt-6 border-t border-[#EDE9DE] text-center">
                  <p className="text-[#78838C] text-xs uppercase tracking-wider mb-1">Best for</p>
                  <p className="text-[#263238] text-sm font-medium">Self-driven individuals who prefer learning and managing finances on their own</p>
                </div>
              </div>
            </div>

            {/* Guided Card */}
            <div className="relative group" data-testid="guided-card">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-[#D4A84F] to-[#B18A3A] rounded-2xl opacity-20 group-hover:opacity-40 blur transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-2xl p-8 border border-[#D4A84F]/30 hover:border-[#D4A84F]/60 transition-all h-full flex flex-col shadow-lg shadow-[#0F4C5C]/5">
                {/* Popular badge */}
                <div className="absolute -top-3 right-6 px-4 py-1 bg-gradient-to-r from-[#D4A84F] to-[#B18A3A] text-[#263238] text-xs font-semibold rounded-full shadow-md">
                  RECOMMENDED
                </div>

                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#D4A84F] to-[#B18A3A] flex items-center justify-center flex-shrink-0 shadow-md shadow-[#D4A84F]/30">
                    <FaHandshake className="text-[#263238] text-xl" />
                  </div>
                  <div>
                    <h3 className="font-poppins text-2xl font-semibold text-[#0F4C5C]">Guided</h3>
                    <p className="text-[#B18A3A] text-sm font-medium">By Certified Financial Consultants</p>
                  </div>
                </div>

                <p className="text-[#4E5B65] text-sm mb-6 leading-relaxed">
                  Get everything in DIY <span className="text-[#B18A3A] font-semibold">plus</span> dedicated support from qualified financial planners who review, guide, and help you make informed decisions at every step.
                </p>

                <div className="space-y-3 mb-4 flex-grow">
                  {[
                    "Everything in DIY — Full Platform Access",
                    "Periodic Financial Reviews with Certified Consultants",
                    "Personalized Goal Planning & Strategy Sessions",
                    "Insurance & Investment Portfolio Review",
                    "Tax-Aware Planning Guidance",
                    "Family Financial Health Check-ups",
                    "Priority Support & Query Resolution",
                    "Retirement & Estate Planning Consultation"
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <FaCheck className="text-[#D4A84F] mt-1 flex-shrink-0 text-xs" />
                      <span className="text-[#263238] text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-auto pt-6 border-t border-[#EDE9DE] text-center">
                  <p className="text-[#78838C] text-xs uppercase tracking-wider mb-1">Best for</p>
                  <p className="text-[#263238] text-sm font-medium">Families & professionals who want expert-led, structured financial planning with accountability</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Arth Gurukul Section */}
      <section id="why-arth-kundli" className="py-20 relative" data-testid="why-arth-kundli-section">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 right-20 w-80 h-80 bg-[#D4A84F]/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-20 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-[#0F4C5C] text-white rounded-full text-sm font-semibold mb-4 shadow-md shadow-[#0F4C5C]/25 tracking-wide">
                Why Choose Us
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#263238] mb-6">
                Why <span className="text-[#D4A84F]">Arth Gurukul</span>?
              </h2>
              <p className="text-lg text-[#4E5B65] mb-4">
                Because your finances deserve more than guesswork.
              </p>
              <p className="text-base text-[#78838C] max-w-3xl mx-auto">
                Arth Gurukul is not just another financial tool—it's a complete, goal-driven financial system designed to give you <span className="text-[#263238] font-semibold">clarity, control, and confidence</span>.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-10">
              {[
                { icon: FaChartPie, text: "360° Financial Clarity", desc: "See your entire financial life in one place" },
                { icon: FaBullseye, text: "Goal-Based Planning", desc: "Every rupee mapped to a purpose" },
                { icon: FaCalculator, text: "Scientific Gap Analysis", desc: "Know exactly where you stand and what's missing" },
                { icon: FaChartLine, text: "Live Progress Tracking", desc: "Stay on track with real-time insights" },
                { icon: FaHandshake, text: "Expert Guidance", desc: "Regular reviews with certified financial consultants" },
                { icon: FaBrain, text: "AI-Powered Insights", desc: "Smart data, simple decisions" },
                { icon: FaGraduationCap, text: "Financial Education", desc: "Learn, grow, and secure your future together" },
                { icon: FaLock, text: "Secure Financial Vault", desc: "Everything your family should know, in one place" },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-white/95 rounded-xl border border-[#EDE9DE] hover:border-[#D4A84F]/40 transition-colors shadow-sm shadow-[#0F4C5C]/5">
                  <div className="w-10 h-10 rounded-lg bg-[#D4A84F]/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="text-[#D4A84F]" />
                  </div>
                  <div>
                    <h4 className="text-[#263238] font-semibold text-sm">{item.text}</h4>
                    <p className="text-[#78838C] text-xs mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-[#D4A84F]/10 to-blue-500/10 rounded-2xl p-6 md:p-8 border border-[#D4A84F]/20 text-center">
              <p className="text-[#4E5B65] text-base mb-4">
                Built & created by a team of <span className="text-[#263238] font-semibold">highly qualified and experienced financial professionals</span> from the financial services industry, education, coaching, and training.
              </p>
              <p className="text-lg text-[#4E5B65] mb-6">
                All of this at a price that's less than what you spend on a <span className="text-[#D4A84F] font-bold">single pizza</span> in a year.
              </p>
              <div className="inline-block">
                <h3 className="text-2xl font-bold text-[#D4A84F]">Arth Gurukul</h3>
                <p className="text-[#78838C] text-sm tracking-widest uppercase">Your School for Lifelong Financial Wellness</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Financial Challenges & Solutions Section */}
      <section className="py-20 bg-gradient-to-b from-[#F8F6F0] to-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#D4A84F]/10 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-red-600 text-white rounded-full text-sm font-semibold mb-4 shadow-md shadow-red-600/30 tracking-wide">
              The Real Problems
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#263238] mb-4">
              Financial Challenges Faced by <span className="text-[#D4A84F]">Indian Families</span>
            </h2>
            <p className="text-[#78838C] text-lg max-w-3xl mx-auto">
              Financial stress in India is rarely about income alone. It is about <span className="text-[#263238]">clarity, structure, discipline, and informed decision-making</span>. Here's how Arth Gurukul addresses these critical gaps.
            </p>
          </div>

          {/* Challenges Grid */}
          <div className="space-y-8 max-w-6xl mx-auto">
            {financialChallenges.map((challenge) => (
              <div 
                key={challenge.id}
                className="grid lg:grid-cols-2 gap-6 items-stretch"
              >
                {/* Problem Card - Always Left */}
                <div className="bg-gradient-to-br from-white to-[#FDFCF9] rounded-2xl p-6 border border-red-500/30 hover:border-red-500/50 transition-all">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
                      <FaExclamationTriangle className="text-red-400 text-xl" />
                    </div>
                    <div>
                      <span className="text-red-400 text-sm font-medium">Challenge #{challenge.id}</span>
                      <h3 className="text-xl font-bold text-[#263238]">{challenge.problem}</h3>
                      {challenge.subtitle && (
                        <p className="text-[#78838C] text-sm italic">{challenge.subtitle}</p>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-[#4E5B65] mb-4">{challenge.description}</p>
                  
                  <div className="space-y-2">
                    <p className="text-red-400 text-sm font-medium">This leads to:</p>
                    {challenge.painPoints.map((point, i) => (
                      <div key={i} className="flex items-center gap-2 text-[#78838C] text-sm">
                        <span className="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
                        {point}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Solution Card - Always Right */}
                <div className="bg-gradient-to-br from-white to-[#FDFCF9] rounded-2xl p-6 border border-[#D4A84F]/30 hover:border-[#D4A84F]/50 transition-all">
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${challenge.color} flex items-center justify-center flex-shrink-0`}>
                      <challenge.icon className="text-[#263238] text-xl" />
                    </div>
                    <div>
                      <span className="text-[#D4A84F] text-sm font-medium">Solution</span>
                      <h3 className="text-xl font-bold text-[#D4A84F]">{challenge.solution}</h3>
                    </div>
                  </div>
                  
                  <p className="text-[#4E5B65]">{challenge.solutionDesc}</p>
                  
                  <div className="mt-4 pt-4 border-t border-[#EDE9DE]">
                    <div className="flex items-center gap-2 text-green-400 text-sm">
                      <FaCheckCircle />
                      <span>Available in Arth Gurukul</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-[#D4A84F]/10 to-[#B18A3A]/10 rounded-2xl p-8 border border-[#D4A84F]/30 max-w-4xl mx-auto">
              <h3 className="text-2xl md:text-3xl font-bold text-[#263238] mb-4">
                From Financial <span className="text-red-400">Confusion</span> to Financial <span className="text-[#D4A84F]">Clarity</span>
              </h3>
              <p className="text-[#4E5B65] text-lg mb-6 max-w-2xl mx-auto">
                Arth Gurukul is not a product marketplace. It is a structured financial education and planning ecosystem that helps Indian families:
              </p>
              <div className="flex flex-wrap justify-center gap-3 mb-6">
                {[
                  "Track money intelligently",
                  "Plan goals scientifically",
                  "Protect adequately",
                  "Invest strategically",
                  "Educate continuously"
                ].map((item, i) => (
                  <div key={i} className="flex items-center px-4 py-2 bg-white/95 rounded-full border border-[#EDE9DE] shadow-sm">
                    <FaCheck className="text-[#D4A84F] mr-2" />
                    <span className="text-[#263238] text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-xl text-[#D4A84F] font-semibold italic mb-6">
                Move from financial guesswork to financial precision.
              </p>
              <TalkToConsultantButton size="large" className="inline-flex items-center justify-center" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 bg-[#F8F6F0]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-[#0F4C5C] text-white rounded-full text-sm font-semibold mb-4 shadow-md shadow-[#0F4C5C]/25 tracking-wide">
              What You Get
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#263238] mb-4">
              Everything You Get Across <span className="text-[#D4A84F]">Free, DIY &amp; Guided</span> Plans
            </h2>
            <p className="text-[#78838C] text-lg max-w-2xl mx-auto">
              14+ premium features designed to transform your financial life — starting from ₹0
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group relative bg-gradient-to-br from-white to-[#FDFCF9] rounded-2xl p-6 border border-[#EDE9DE] hover:border-[#D4A84F]/50 transition-all duration-300 hover:transform hover:-translate-y-1"
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="text-[#263238] text-2xl" />
                </div>
                <h3 className="text-lg font-bold text-[#263238] mb-2">{feature.title}</h3>
                <p className="text-[#78838C] text-sm">{feature.description}</p>
                <div className="absolute top-4 right-4 text-[#D4A84F]/20 font-bold text-3xl">
                  {String(index + 1).padStart(2, '0')}
                </div>
              </div>
            ))}

            {/* Portfolio Review Card */}
            <div className="group relative bg-gradient-to-br from-[#D4A84F]/20 to-[#B18A3A]/10 rounded-2xl p-6 border border-[#D4A84F]/30 hover:border-[#D4A84F] transition-all duration-300">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-[#D4A84F] to-[#B18A3A] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <FaClipboardCheck className="text-[#263238] text-2xl" />
              </div>
              <h3 className="text-lg font-bold text-[#D4A84F] mb-2">Free Portfolio Review</h3>
              <p className="text-[#4E5B65] text-sm">Annual professional review to evaluate performance, identify gaps, improve allocation & align with goals.</p>
              <div className="absolute top-4 right-4 text-[#D4A84F]/30 font-bold text-3xl">13</div>
            </div>

            {/* Money Meter Card */}
            <div className="group relative bg-gradient-to-br from-white to-[#FDFCF9] rounded-2xl p-6 border border-[#EDE9DE] hover:border-[#D4A84F]/50 transition-all duration-300 hover:transform hover:-translate-y-1">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <FaCalculator className="text-[#263238] text-2xl" />
              </div>
              <h3 className="text-lg font-bold text-[#263238] mb-2">Money Meter - Your Monthly Budget Planner</h3>
              <p className="text-[#78838C] text-sm">Track your monthly income and expenses, set budgets for different categories, and monitor your spending to achieve your financial goals.</p>
              <div className="absolute top-4 right-4 text-[#D4A84F]/20 font-bold text-3xl">14</div>
            </div>
          </div>
        </div>
      </section>

      {/* Value-Added Services Section */}
      <section className="py-20 bg-gradient-to-b from-[#F8F6F0] to-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#D4A84F] rounded-full filter blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#00BFFF] rounded-full filter blur-[150px]" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-[#0F4C5C] text-white rounded-full text-sm font-semibold mb-4 shadow-md shadow-[#0F4C5C]/25 tracking-wide">
              Free Value-Added Services
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#263238] mb-4">
              Services Designed For Every Life Stage
            </h2>
            <p className="text-[#78838C] text-lg max-w-2xl mx-auto">
              Comprehensive financial tools and assessments tailored to your needs — all included with your subscription
            </p>
          </div>

          <div className="space-y-10">

            {/* Parents */}
            <div className="bg-gradient-to-br from-white to-[#FDFCF9] rounded-2xl border border-[#EDE9DE] overflow-hidden">
              <div className="flex items-center gap-3 px-6 py-4 border-b border-[#EDE9DE] bg-gradient-to-r from-pink-500/10 to-transparent">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-pink-500 to-rose-500 flex items-center justify-center">
                  <FaChild className="text-[#263238]" />
                </div>
                <h3 className="text-xl font-bold text-[#263238]">Parents</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-4 p-6">
                <div className="bg-white rounded-xl p-5 border border-[#EDE9DE] shadow-sm shadow-[#0F4C5C]/5 hover:border-pink-500/40 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-pink-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FaGraduationCap className="text-pink-400 text-sm" />
                    </div>
                    <div>
                      <h4 className="text-[#263238] font-bold text-base mb-2">Shiksha Sankalp</h4>
                      <p className="text-[#78838C] text-sm leading-relaxed">Dreams are big. Education costs are bigger. Shiksha Sankalp helps parents of kids (0–11 years) plan early, invest wisely, and stay stress-free about their child's education.</p>
                      <p className="text-pink-400 text-xs mt-2 font-medium">Available in Hindi, English, Gujarati & Marathi with a FREE report</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-5 border border-[#EDE9DE] shadow-sm shadow-[#0F4C5C]/5 hover:border-pink-500/40 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-pink-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FaBullseye className="text-pink-400 text-sm" />
                    </div>
                    <div>
                      <h4 className="text-[#263238] font-bold text-base mb-2">Goal Manthan</h4>
                      <p className="text-[#78838C] text-sm leading-relaxed">Industry-first comprehensive career and education goal planning test for Class 6th to 12th students with a detailed AI-powered report.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Owners */}
            <div className="bg-gradient-to-br from-white to-[#FDFCF9] rounded-2xl border border-[#EDE9DE] overflow-hidden">
              <div className="flex items-center gap-3 px-6 py-4 border-b border-[#EDE9DE] bg-gradient-to-r from-amber-500/10 to-transparent">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-amber-500 to-yellow-500 flex items-center justify-center">
                  <FaHome className="text-[#263238]" />
                </div>
                <h3 className="text-xl font-bold text-[#263238]">Business Owners</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-4 p-6">
                <div className="bg-white rounded-xl p-5 border border-[#EDE9DE] shadow-sm shadow-[#0F4C5C]/5 hover:border-amber-500/40 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FaShieldAlt className="text-amber-400 text-sm" />
                    </div>
                    <div>
                      <h4 className="text-[#263238] font-bold text-base mb-2">Business Risk Assessment</h4>
                      <p className="text-[#78838C] text-sm leading-relaxed">Financial Health Checkup for Businesses (MSME). Auto-calculates recommended covers — Keyman, Partnership/Shareholder, Group Health, Fire/Asset, Liability — with adequacy %, risk points, and overall risk level.</p>
                      <p className="text-amber-400 text-xs mt-2 font-medium">Risk Level: LOW / MEDIUM / HIGH / CRITICAL</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-5 border border-[#EDE9DE] shadow-sm shadow-[#0F4C5C]/5 hover:border-amber-500/40 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FaExclamationTriangle className="text-amber-400 text-sm" />
                    </div>
                    <div>
                      <h4 className="text-[#263238] font-bold text-base mb-2">MSME Risk MRI</h4>
                      <p className="text-[#78838C] text-sm leading-relaxed">A powerful Business Risk Diagnostic System designed to detect hidden financial and operational risks in MSME businesses. Just like a medical MRI scans the body, MSME Risk MRI scans your business to identify risk exposures before they become costly losses.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Salaried */}
            <div className="bg-gradient-to-br from-white to-[#FDFCF9] rounded-2xl border border-[#EDE9DE] overflow-hidden">
              <div className="flex items-center gap-3 px-6 py-4 border-b border-[#EDE9DE] bg-gradient-to-r from-blue-500/10 to-transparent">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                  <FaWallet className="text-[#263238]" />
                </div>
                <h3 className="text-xl font-bold text-[#263238]">Salaried Professionals</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-4 p-6">
                <div className="bg-white rounded-xl p-5 border border-[#EDE9DE] shadow-sm shadow-[#0F4C5C]/5 hover:border-blue-500/40 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FaBrain className="text-blue-400 text-sm" />
                    </div>
                    <div>
                      <h4 className="text-[#263238] font-bold text-base mb-2">My Money Personality Test</h4>
                      <p className="text-[#78838C] text-sm leading-relaxed">Discover your unique money personality with a detailed report. Understand your financial behaviour patterns and get personalized recommendations.</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-5 border border-[#EDE9DE] shadow-sm shadow-[#0F4C5C]/5 hover:border-blue-500/40 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FaChartPie className="text-blue-400 text-sm" />
                    </div>
                    <div>
                      <h4 className="text-[#263238] font-bold text-base mb-2">Arth Kundli – Goal-Based Financial Planning &amp; Review</h4>
                      <p className="text-[#78838C] text-sm leading-relaxed">This free service helps you gain complete clarity on your existing insurance coverage, fund investments, risk exposure, and goal alignment.</p>
                      <p className="text-blue-400 text-xs mt-2 font-medium">FREE comprehensive review</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Young Professionals */}
            <div className="bg-gradient-to-br from-white to-[#FDFCF9] rounded-2xl border border-[#EDE9DE] overflow-hidden">
              <div className="flex items-center gap-3 px-6 py-4 border-b border-[#EDE9DE] bg-gradient-to-r from-emerald-500/10 to-transparent">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 flex items-center justify-center">
                  <FaRocket className="text-[#263238]" />
                </div>
                <h3 className="text-xl font-bold text-[#263238]">Young Professionals</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-4 p-6">
                <div className="bg-white rounded-xl p-5 border border-[#EDE9DE] shadow-sm shadow-[#0F4C5C]/5 hover:border-emerald-500/40 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FaHeart className="text-emerald-400 text-sm" />
                    </div>
                    <div>
                      <h4 className="text-[#263238] font-bold text-base mb-2">Debt Detox 360</h4>
                      <p className="text-[#78838C] text-sm leading-relaxed">This free service helps you gain complete clarity on existing loans & coverage. Consolidate all debt into a single debt or close them before term to get debt-free.</p>
                      <p className="text-emerald-400 text-xs mt-2 font-medium">FREE debt consolidation analysis</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-5 border border-[#EDE9DE] shadow-sm shadow-[#0F4C5C]/5 hover:border-emerald-500/40 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FaRegLightbulb className="text-emerald-400 text-sm" />
                    </div>
                    <div>
                      <h4 className="text-[#263238] font-bold text-base mb-2">Pathfinder — Job Switch Career Test</h4>
                      <p className="text-[#78838C] text-sm leading-relaxed">Thinking of a career switch? Take the Pathfinder test and get a comprehensive 75+ page report with insights on your ideal career path, strengths, and growth areas.</p>
                      <p className="text-emerald-400 text-xs mt-2 font-medium">75+ Page Detailed Career Report</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Retired */}
            <div className="bg-gradient-to-br from-white to-[#FDFCF9] rounded-2xl border border-[#EDE9DE] overflow-hidden">
              <div className="flex items-center gap-3 px-6 py-4 border-b border-[#EDE9DE] bg-gradient-to-r from-purple-500/10 to-transparent">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-purple-500 to-violet-500 flex items-center justify-center">
                  <FaUserShield className="text-[#263238]" />
                </div>
                <h3 className="text-xl font-bold text-[#263238]">Retired</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-4 p-6">
                <div className="bg-white rounded-xl p-5 border border-[#EDE9DE] shadow-sm shadow-[#0F4C5C]/5 hover:border-purple-500/40 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FaBalanceScale className="text-purple-400 text-sm" />
                    </div>
                    <div>
                      <h4 className="text-[#263238] font-bold text-base mb-2">Estate Planning</h4>
                      <p className="text-[#78838C] text-sm leading-relaxed">Comprehensive estate planning guidance to ensure smooth wealth transfer, will preparation, and legacy management for your loved ones.</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-5 border border-[#EDE9DE] shadow-sm shadow-[#0F4C5C]/5 hover:border-purple-500/40 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FaHeart className="text-purple-400 text-sm" />
                    </div>
                    <div>
                      <h4 className="text-[#263238] font-bold text-base mb-2">Retirement & Health Insurance Portfolio Review</h4>
                      <p className="text-[#78838C] text-sm leading-relaxed">Expert review of your retirement corpus, pension plans, and health insurance coverage to ensure adequate protection and income during your golden years.</p>
                      <p className="text-purple-400 text-xs mt-2 font-medium">FREE portfolio review service</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Pricing Section - 3-Tier (Free / DIY / Guided) */}
      <section id="pricing" className="py-20" data-testid="pricing-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[#0F4C5C] text-white rounded-full text-sm font-semibold mb-4 shadow-md shadow-[#0F4C5C]/25 tracking-wide">
              Simple, Transparent Pricing
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#263238] mb-4">
              Pick the Path That <span className="text-[#D4A84F]">Fits You Best</span>
            </h2>
            <p className="text-[#78838C] text-base max-w-2xl mx-auto">
              Start free, learn at your own pace with our DIY toolkit, or get hand-held by certified advisors with our Guided plan.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* TIER 1 — FREE */}
            <div className="relative bg-white rounded-2xl p-8 border border-[#EDE9DE] flex flex-col shadow-lg shadow-[#0F4C5C]/5" data-testid="plan-free-card">
              <div className="mb-6">
                <h3 className="font-poppins text-2xl font-semibold text-[#0F4C5C] mb-1">Free</h3>
                <p className="text-[#78838C] text-sm">Try Arth Gurukul, no payment needed</p>
              </div>
              <div className="mb-6">
                <span className="font-poppins text-4xl font-semibold text-[#0F4C5C]">₹0</span>
                <span className="text-[#78838C] text-sm ml-2">/ year</span>
                <p className="text-[#78838C] text-xs mt-1">1-year validity, no card needed</p>
              </div>
              <div className="space-y-3 mb-8 flex-grow">
                {[
                  "My Family Should Know (Family Vault)",
                  "Profile &amp; Personal Info",
                  "DigiLocker Document Access",
                  "Dashboard Overview",
                  "Announcements &amp; Updates",
                  "Paiso Ki Pathshala (YouTube videos)"
                ].map((feature) => (
                  <div key={`free-${feature.slice(0,12)}`} className="flex items-start gap-2.5">
                    <FaCheck className="text-[#0F4C5C] flex-shrink-0 text-xs mt-1" />
                    <span
                      className="text-[#263238] text-sm"
                      dangerouslySetInnerHTML={{ __html: feature }}
                    />
                  </div>
                ))}
              </div>
              <Link
                to="/register"
                className="w-full py-3 text-center bg-transparent border-2 border-[#0F4C5C] text-[#0F4C5C] font-semibold rounded-xl hover:bg-[#0F4C5C] hover:text-white transition-all"
                data-testid="pricing-free-btn"
              >
                Join for Free
              </Link>
            </div>

            {/* TIER 2 — DIY */}
            <div className="relative bg-white rounded-2xl p-8 border-2 border-[#16A085]/50 flex flex-col shadow-xl shadow-[#16A085]/10" data-testid="plan-diy-card">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-[#16A085] to-[#0D6A57] text-white text-xs font-semibold rounded-full shadow-md">
                MOST POPULAR — DIY
              </div>
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-poppins text-2xl font-semibold text-[#0F4C5C]">DIY</h3>
                  <span className="px-2 py-0.5 bg-[#E8F7F3] text-[#0D6A57] border border-[#16A085]/40 text-[10px] font-semibold uppercase tracking-wide rounded">Self-Paced</span>
                </div>
                <p className="text-[#78838C] text-sm">Do-It-Yourself financial education &amp; tools</p>
              </div>
              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="font-poppins text-4xl font-semibold text-[#16A085]">₹999</span>
                  <span className="text-[#78838C] text-sm">/ year + GST</span>
                </div>
                <p className="text-[#78838C] text-xs mt-1">All DIY tools, unlimited access</p>
              </div>

              {/* Plan-level disclaimer */}
              <p className="text-[10px] text-[#78838C] italic mb-5 border-l-2 border-[#16A085]/40 pl-2 leading-relaxed" data-testid="pricing-disclaimer-diy">
                The membership fee is for financial wellness education &amp; financial wellness tools only. No fees are charged by the platform for any consultation or advice by a financial consultant or planner.
              </p>
              <div className="space-y-3 mb-8 flex-grow">
                {[
                  "Everything in Free",
                  "Money Meter — Cash-flow &amp; leakage check",
                  "Money School — Full library access",
                  "Paiso Ki Pathshala — Articles, PDFs &amp; videos",
                  "My Money Personality Test",
                  "Your Child Future Ready? Test",
                  "Financial Health Check-Up",
                  "All Future DIY Tools"
                ].map((feature) => (
                  <div key={`diy-${feature.slice(0,12)}`} className="flex items-start gap-2.5">
                    <FaCheck className="text-[#16A085] flex-shrink-0 text-xs mt-1" />
                    <span
                      className="text-[#263238] text-sm"
                      dangerouslySetInnerHTML={{ __html: feature }}
                    />
                  </div>
                ))}
              </div>
              <TalkToConsultantButton size="large" className="w-full" />
            </div>

            {/* TIER 3 — GUIDED */}
            <div className="relative bg-white rounded-2xl p-8 border-2 border-[#D4A84F]/55 flex flex-col shadow-xl shadow-[#D4A84F]/10" data-testid="plan-guided-card">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-[#D4A84F] to-[#B18A3A] text-[#263238] text-xs font-semibold rounded-full shadow-md">
                BEST VALUE — GUIDED
              </div>
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-poppins text-2xl font-semibold text-[#0F4C5C]">Guided</h3>
                  <span className="px-2 py-0.5 bg-[#FBF6EA] text-[#846829] border border-[#D4A84F]/45 text-[10px] font-semibold uppercase tracking-wide rounded">Advisor-Led</span>
                </div>
                <p className="text-[#78838C] text-sm">DIY + expert advisor guidance</p>
              </div>
              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="text-[#78838C] line-through text-lg">₹4,999</span>
                  <span className="font-poppins text-4xl font-semibold text-[#B18A3A]">₹2,999</span>
                  <span className="text-[#78838C] text-sm">/ year + GST</span>
                </div>
                <div className="inline-block mt-2 bg-red-50 text-red-600 text-xs font-semibold px-3 py-1 rounded-full border border-red-200">
                  SAVE 40% — LIMITED TIME
                </div>
              </div>

              {/* Plan-level disclaimer */}
              <p className="text-[10px] text-[#78838C] italic mb-5 border-l-2 border-[#D4A84F]/40 pl-2 leading-relaxed" data-testid="pricing-disclaimer-guided">
                The membership fee is for financial wellness education &amp; financial wellness tools only. No fees are charged by the platform for any consultation or advice by a financial consultant or planner.
              </p>
              <div className="space-y-3 mb-8 flex-grow">
                {[
                  "Everything in DIY",
                  "Arth Kundli — Goal-Based Financial Plan",
                  "Shiksha Sankalp, Goal Manthan, Pathfinder",
                  "Debt Detox 360, Business Risk MRI",
                  "Insurance, Retirement &amp; Wealth Reviews",
                  "Mutual Fund &amp; Child-Education Reviews",
                  "Personalised PDF Reports",
                  "Direct Expert Advisor Connect"
                ].map((feature) => (
                  <div key={`guided-${feature.slice(0,12)}`} className="flex items-start gap-2.5">
                    <FaCheck className="text-[#D4A84F] flex-shrink-0 text-xs mt-1" />
                    <span
                      className="text-[#263238] text-sm"
                      dangerouslySetInnerHTML={{ __html: feature }}
                    />
                  </div>
                ))}
              </div>
              <TalkToConsultantButton size="large" className="w-full" />
            </div>
          </div>

          <p className="text-center text-[#78838C] text-sm mt-8 italic">
            "Not just a subscription — a complete financial life system."
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gradient-to-b from-[#F8F6F0] to-white relative" data-testid="faq-section">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-1/4 w-72 h-72 bg-[#D4A84F]/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-1/4 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[#0F4C5C] text-white rounded-full text-sm font-semibold mb-4 shadow-md shadow-[#0F4C5C]/25 tracking-wide">
              Got Questions?
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#263238] mb-4">
              Frequently Asked <span className="text-[#D4A84F]">Questions</span>
            </h2>
            <p className="text-[#78838C] text-base max-w-2xl mx-auto">
              Everything you need to know about Arth Gurukul before you get started.
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-3">
            {[
              {
                q: "What exactly is Arth Gurukul?",
                a: "Arth Gurukul is a goal-based financial planning and education platform. It helps you see your complete financial picture — income, expenses, insurance, investments, loans, and goals — all in one place. Think of it as a GPS for your money: it shows where you are, where you want to go, and the best route to get there."
              },
              {
                q: "I'm not a finance expert. Can I still use this?",
                a: "Absolutely! Arth Gurukul is designed for everyday people — salaried professionals, business owners, homemakers, and retirees. The interface is simple and intuitive, with guided steps and plain-language explanations. You also get access to certified financial consultants who can help you understand your reports."
              },
              {
                q: "How is this different from a mutual fund app or stock trading platform?",
                a: "Mutual fund and trading apps help you buy/sell investments. Arth Gurukul goes much deeper — it maps every rupee to a life goal (retirement, child's education, home, emergency fund), identifies financial gaps, and gives you a structured plan. It's not about buying products; it's about building a complete financial life system."
              },
              {
                q: "How does pricing work? What are DIY and Guided plans?",
                a: "We offer three tiers. Free gives you the Family Vault, Profile, and free Paiso Ki Pathshala videos (1-year validity, no card). DIY (₹999 + GST/year) unlocks all self-paced tools — Money Meter, Money School, full Paiso Ki Pathshala library, Money Personality Test, Child Future Ready Test and Financial Health Check-Up. Guided (₹2,999 + GST/year, regular ₹4,999) adds advisor-led services — Arth Kundli goal-based plan, Shiksha Sankalp, Goal Manthan, Pathfinder, Debt Detox 360, Business Risk MRI, plus Insurance/Retirement/Wealth/MF/Child-Education portfolio reviews and direct advisor connect."
              },
              {
                q: "Is my financial data safe on Arth Gurukul?",
                a: "Yes. Data security is our top priority. All data is encrypted in transit and at rest using industry-standard protocols. We use role-based access control so only authorized users can view your information. We do not share, sell, or use your personal financial data for any third-party purpose. Your data belongs to you."
              },
              {
                q: "Can my financial advisor access my data?",
                a: "Only if you are connected to a financial advisor through the platform. Your advisor can view your financial profile and goals to provide better guidance — but they cannot modify your data without your knowledge. You remain in full control of what you share."
              },
              {
                q: "What if I forget my password or get locked out?",
                a: "You can reset your password anytime using the 'Forgot Password' option on the login page. If you face any issues, our support team is available to help you regain access quickly."
              },
              {
                q: "Can I cancel my subscription? Is there a refund policy?",
                a: "Since Arth Gurukul is a digital service platform providing instant access to tools and content, all sales are final and no refunds are applicable once the subscription is activated. However, you can choose not to renew when your annual subscription expires."
              },
              {
                q: "Who built Arth Gurukul?",
                a: "Arth Gurukul is built and maintained by a team of highly qualified financial professionals, educators, coaches, and technology experts from the financial services industry. It is powered by MDRT School and Financial Nirvana."
              },
              {
                q: "Does Arth Gurukul give investment advice or sell financial products?",
                a: "No. Arth Gurukul is strictly a financial education and planning tool. It does not provide investment advice, sell mutual funds, insurance, or any financial product. All insights are for educational purposes. For specific investment decisions, always consult a qualified financial advisor."
              }
            ].map((faq, index) => (
              <div
                key={index}
                className={`rounded-xl border transition-all duration-300 ${openFaq === index ? 'bg-white border-[#D4A84F]/40 shadow-md shadow-[#0F4C5C]/8' : 'bg-white/70 border-[#EDE9DE] hover:border-[#0F4C5C]/40'}`}
                data-testid={`faq-item-${index}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left"
                  data-testid={`faq-toggle-${index}`}
                >
                  <span className="text-[#263238] font-medium text-sm sm:text-base pr-4">{faq.q}</span>
                  <FaChevronDown className={`text-[#D4A84F] flex-shrink-0 transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <p className="px-6 pb-5 text-[#78838C] text-sm leading-relaxed">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#0F4C5C] via-[#093039] to-[#0F4C5C]">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="font-poppins text-3xl md:text-4xl font-semibold text-white mb-6">
              Because Your Financial Future Deserves More Than Guesswork
            </h2>
            <p className="text-white/80 text-lg mb-8">
              One small annual subscription. A lifetime of financial awareness.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <TalkToConsultantButton size="large" />
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white/80 text-white font-semibold rounded-xl hover:bg-white/15 transition-all"
              >
                Get Started Now
                <FaArrowRight className="ml-2" />
              </Link>
              <InstallAppButton
                variant="hero"
                label="Install as App"
                className="px-8 py-4 text-base"
              />
            </div>
            <p className="text-white/70 text-xs mt-4">
              Install Arth Gurukul to your phone home screen — one tap, no store required.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-[#EDE9DE]">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <p className="text-[#78838C] text-sm mb-4">
              © 2026 Arth Gurukul | Tech, Tools &amp; Training Powered by MDRT School | By Financial Nirvana. All rights reserved.
            </p>
            <p className="text-[#78838C] text-xs max-w-4xl mx-auto mb-4">
              Disclaimer: Arth Gurukul is a financial education & planning tool platform. The information and tools provided are for educational purposes only and should not be construed as financial advice. Please consult with a qualified financial advisor before making any investment decisions. Past performance is not indicative of future results.
            </p>
            <p className="text-[#78838C] text-xs max-w-4xl mx-auto">
              MDRT School and Financial Nirvana do not provide any financial, investment, legal, or tax advisory services. We are a capability development and education platform offering tools, technology, and training exclusively for financial services consultants & sales professionals. Any decisions taken based on the use of our content, tools, or programs are solely the responsibility of the user.
            </p>
            
            {/* Legal Links */}
            <div className="mt-6 flex justify-center gap-6 flex-wrap">
              <button 
                onClick={() => setShowTermsModal(true)}
                className="text-[#D4A84F] hover:text-[#FFA500] text-sm font-medium transition-colors"
              >
                Terms & Conditions
              </button>
              <span className="text-gray-600">|</span>
              <button 
                onClick={() => setShowPrivacyModal(true)}
                className="text-[#D4A84F] hover:text-[#FFA500] text-sm font-medium transition-colors"
              >
                Privacy Policy
              </button>
              <span className="text-gray-600">|</span>
              <button 
                onClick={() => setShowRefundModal(true)}
                className="text-[#D4A84F] hover:text-[#FFA500] text-sm font-medium transition-colors"
              >
                Refund Policy
              </button>
              <span className="text-gray-600">|</span>
              <button 
                onClick={() => setShowDataSecurityModal(true)}
                className="text-[#D4A84F] hover:text-[#FFA500] text-sm font-medium transition-colors"
                data-testid="data-security-policy-btn"
              >
                Data Security Policy
              </button>
            </div>
            
            <div className="mt-6 flex justify-center gap-4">
              <span className="text-[#78838C] flex items-center text-sm">
                <FaEnvelope className="mr-2" /> Tech &amp; Training Support only: care@mdrtschool.com
              </span>
            </div>
          </div>
        </div>
      </footer>

      {/* Terms & Conditions Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl border border-[#EDE9DE] max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-[#EDE9DE] flex items-center justify-between">
              <h2 className="text-2xl font-bold text-[#263238]">Terms & Conditions</h2>
              <button onClick={() => setShowTermsModal(false)} className="text-[#78838C] hover:text-[#263238]">
                <FaTimes size={24} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh] text-[#4E5B65] space-y-4">
              <p className="text-sm text-[#78838C]">Last Updated: January 2026</p>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">1. Acceptance of Terms</h3>
              <p>By accessing and using Arth Gurukul ("the Platform"), you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use the Platform.</p>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">2. Description of Service</h3>
              <p>Arth Gurukul is a financial education and planning tool platform designed for financial advisors and their clients. The Platform provides tools for financial planning, client management, goal tracking, and educational resources.</p>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">3. User Responsibilities</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
                <li>You agree to provide accurate and complete information during registration.</li>
                <li>You shall not use the Platform for any unlawful purpose.</li>
                <li>You are responsible for all activities that occur under your account.</li>
              </ul>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">4. Intellectual Property</h3>
              <p>All content, features, and functionality of the Platform are owned by MDRT School / Financial Nirvana and are protected by intellectual property laws.</p>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">5. Disclaimer</h3>
              <p>The information provided on this Platform is for educational purposes only and should not be construed as financial, investment, legal, or tax advice. Always consult with qualified professionals before making financial decisions.</p>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">6. Limitation of Liability</h3>
              <p>MDRT School and Financial Nirvana shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the Platform.</p>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">7. Modifications</h3>
              <p>We reserve the right to modify these Terms at any time. Continued use of the Platform after changes constitutes acceptance of the modified Terms.</p>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">8. Contact</h3>
              <p>For questions about these Terms, contact us at care@mdrtschool.com</p>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl border border-[#EDE9DE] max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-[#EDE9DE] flex items-center justify-between">
              <h2 className="text-2xl font-bold text-[#263238]">Privacy Policy</h2>
              <button onClick={() => setShowPrivacyModal(false)} className="text-[#78838C] hover:text-[#263238]">
                <FaTimes size={24} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh] text-[#4E5B65] space-y-4">
              <p className="text-sm text-[#78838C]">Last Updated: January 2026</p>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">1. Information We Collect</h3>
              <p>We collect information you provide directly, including:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Personal information (name, email, phone number)</li>
                <li>Financial planning data entered into the Platform</li>
                <li>Usage data and analytics</li>
              </ul>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">2. How We Use Your Information</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li>To provide and maintain the Platform</li>
                <li>To process your transactions</li>
                <li>To send you updates and communications</li>
                <li>To improve our services</li>
              </ul>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">3. Data Security</h3>
              <p>We implement appropriate security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure.</p>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">4. Data Sharing</h3>
              <p>We do not sell your personal information. We may share data with:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Service providers who assist in Platform operations</li>
                <li>Legal authorities when required by law</li>
              </ul>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">5. Your Rights</h3>
              <p>You have the right to access, correct, or delete your personal information. Contact us at care@mdrtschool.com to exercise these rights.</p>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">6. Cookies</h3>
              <p>We use cookies and similar technologies to enhance your experience on the Platform.</p>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">7. Contact</h3>
              <p>For privacy-related inquiries, contact us at care@mdrtschool.com</p>
            </div>
          </div>
        </div>
      )}

      {/* Data Security Policy Modal */}
      {showDataSecurityModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl border border-[#EDE9DE] max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-[#EDE9DE] flex items-center justify-between">
              <h2 className="text-2xl font-bold text-[#263238]">Data Security Policy</h2>
              <button onClick={() => setShowDataSecurityModal(false)} className="text-[#78838C] hover:text-[#263238]" data-testid="close-data-security-modal">
                <FaTimes size={24} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh] text-[#4E5B65] space-y-4">
              <p className="text-sm text-[#78838C]">Last Updated: March 2026</p>
              
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
                <p className="text-blue-200">At Arth Gurukul, we are committed to protecting the security, confidentiality, and integrity of your personal and financial data. This policy outlines the measures we take to safeguard your information.</p>
              </div>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">1. Data Encryption</h3>
              <p>All data transmitted between your device and our servers is encrypted using industry-standard SSL/TLS (256-bit) encryption. Sensitive data such as passwords are hashed using bcrypt before storage and are never stored in plain text.</p>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">2. Access Control</h3>
              <p>We implement strict role-based access controls (RBAC) to ensure that:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Only authorized personnel can access user data</li>
                <li>Financial advisors can only view data of their assigned clients</li>
                <li>Managers have access limited to their team's data</li>
                <li>All access is logged and auditable</li>
              </ul>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">3. Data Storage & Retention</h3>
              <p>Your data is stored on secure, encrypted servers with regular backups. We retain your data only as long as necessary to provide our services or as required by law. Upon account deletion, your personal data will be permanently removed within 90 days.</p>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">4. Third-Party Sharing</h3>
              <p>We do not sell, trade, or rent your personal or financial information to third parties. Data is shared only:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>With your assigned financial advisor for service delivery</li>
                <li>With payment processors for subscription management</li>
                <li>When required by law or legal proceedings</li>
              </ul>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">5. Advisor Data Protection</h3>
              <p>All financial advisors on our platform are bound by a Data Protection & Confidentiality Undertaking. They are contractually obligated to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Maintain strict confidentiality of client information</li>
                <li>Use client data solely for the purpose of financial planning</li>
                <li>Not share, copy, or distribute client data outside the platform</li>
                <li>Report any data breaches immediately</li>
              </ul>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">6. Security Monitoring</h3>
              <p>We continuously monitor our systems for potential vulnerabilities and threats. Our security measures include:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Regular security audits and vulnerability assessments</li>
                <li>Automated intrusion detection systems</li>
                <li>Multi-factor authentication support</li>
                <li>Session timeout and automatic logout for inactive sessions</li>
              </ul>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">7. Incident Response</h3>
              <p>In the unlikely event of a data breach, we will:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Notify affected users within 72 hours</li>
                <li>Take immediate steps to contain and remediate the breach</li>
                <li>Cooperate with relevant authorities as required</li>
                <li>Provide guidance to affected users on protective measures</li>
              </ul>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">8. Your Rights</h3>
              <p>You have the right to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Access your personal data stored on our platform</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your account and associated data</li>
                <li>Export your data in a portable format</li>
              </ul>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">9. Contact Us</h3>
              <p>For any data security concerns or questions, please contact us at:</p>
              <p className="mt-2"><strong>Tech &amp; Training Support only:</strong> care@mdrtschool.com</p>
            </div>
          </div>
        </div>
      )}

      {showRefundModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl border border-[#EDE9DE] max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-[#EDE9DE] flex items-center justify-between">
              <h2 className="text-2xl font-bold text-[#263238]">Refund Policy</h2>
              <button onClick={() => setShowRefundModal(false)} className="text-[#78838C] hover:text-[#263238]">
                <FaTimes size={24} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh] text-[#4E5B65] space-y-4">
              <p className="text-sm text-[#78838C]">Last Updated: January 2026</p>
              
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-bold text-yellow-400 mb-2">Important Notice</h3>
                <p className="text-yellow-200">As Arth Gurukul is a digital service platform, <strong>no refunds are applicable on subscriptions</strong> once activated.</p>
              </div>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">1. Digital Service Policy</h3>
              <p>Arth Gurukul is a digital service platform providing instant access to financial planning tools, educational content, and client management features. Due to the nature of digital services, all sales are final.</p>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">2. No Refund Policy</h3>
              <p>Once your subscription is activated:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>No refunds will be issued for any reason</li>
                <li>Partial refunds are not available</li>
                <li>Unused subscription periods cannot be refunded</li>
                <li>Downgrade requests do not qualify for refunds</li>
              </ul>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">3. Subscription Cancellation</h3>
              <p>You may cancel your subscription at any time. Upon cancellation:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>You will retain access until the end of your current billing period</li>
                <li>No refund will be provided for the remaining period</li>
                <li>Auto-renewal will be disabled</li>
              </ul>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">4. Exceptional Circumstances</h3>
              <p>In rare cases of technical issues preventing service access, we may offer:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Extended subscription period</li>
                <li>Service credits</li>
                <li>Alternative solutions</li>
              </ul>
              <p className="mt-2">These are provided at our sole discretion and do not constitute a refund.</p>
              
              <h3 className="text-lg font-bold text-[#D4A84F]">5. Contact Us</h3>
              <p>For any billing or subscription inquiries, please contact us at:</p>
              <p className="mt-2"><strong>Tech &amp; Training Support only:</strong> care@mdrtschool.com</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
